'use client'

/**
 * Puente a la llamada, con el nombre del niño ya dentro.
 *
 * Aparece solo DESPUÉS de que la herramienta haya producido algo. Ese es el
 * punto: cuando ya existe una carta con el nombre del niño, "que además le
 * llame" continúa lo que el padre acaba de hacer. El mismo CTA antes de darle
 * nada — que es dónde estaba — se lee como un anuncio.
 */

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import type { GuideCharacterKey, GuideLocale } from '@/lib/guides'
import { getGuideCharacterAuthHref } from '@/lib/guides/character-cta'
import { isTrialEnabled } from '@/lib/feature-flags'
import { TRIAL_NOTE_ES } from '@/lib/guides/trial-note'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

export function CallBridge({
  childName,
  character,
  locale,
  title,
  body,
  action,
  placement,
  analyticsProperties,
}: {
  childName: string
  character: GuideCharacterKey
  locale: GuideLocale
  /** Puede llevar `{name}`, que se sustituye por el nombre del niño. */
  title: string
  body: string
  action: string
  /** Distingue en PostHog desde qué herramienta salió el click. */
  placement: string
  analyticsProperties: Record<string, unknown>
}) {
  const trialNote = isTrialEnabled() ? TRIAL_NOTE_ES : null
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 p-5">
      <p className="text-lg font-semibold text-white">{title.replace('{name}', childName)}</p>
      <p className="mt-2 max-w-xl text-base text-white/80">{body}</p>
      <div className="mt-4">
        <Button
          asChild
          className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
        >
          <Link
            href={getGuideCharacterAuthHref(character, locale)}
            onClick={() =>
              captureProductEvent(PRODUCT_EVENTS.GUIDE_CTA_CLICKED, {
                ...analyticsProperties,
                placement,
              })
            }
          >
            {action}
          </Link>
        </Button>
      </div>
      {trialNote && <p className="mt-3 text-sm text-white/60">{trialNote}</p>}
    </div>
  )
}
